// ============================================================================
// HAI Reporting Tool - Care Level Insights Hook
// Aggregates Firestore data to produce patient → provincial level metrics
// ============================================================================

import { useEffect, useMemo, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '@/services/firebase';
import type {
  DeviceSnapshot,
  Facility,
  HCAIRecord,
  HCAICases,
  HCAISurveillanceCase,
  PatientRecord,
  SSISurveillance,
  Ward,
  DeviceDays
} from '@/types';
import {
  aggregateHCAICases,
  aggregateHCAISurveillanceCases,
  calculateDeviceDays,
  calculateHCAIRates
} from '@/utils/calculations';

interface InfectionBreakdownItem {
  label: string;
  count: number;
}

interface PatientLevelInsights {
  totalPatients: number;
  patientsWithDevices: number;
  infectionBreakdown: InfectionBreakdownItem[];
  topRiskFactors: InfectionBreakdownItem[];
  antibioticUsage: InfectionBreakdownItem[];
  microbiologyFindings: InfectionBreakdownItem[];
}

export interface WardLevelInsight {
  wardId: string;
  wardName: string;
  facilityId: string;
  facilityName: string;
  compliance: number;
  deviceSnapshots: number;
  haiRecords: number;
  ssiRecords: number;
  utiRate: number;
  cabsiRate: number;
  vapRate: number;
  ssiRate?: number;
  deviceDays: DeviceDays;
  hcaiCases: HCAICases;
  ssiProcedures?: number;
  ssiInfections?: number;
}

interface WardLevelInsights {
  complianceRate: number;
  wards: WardLevelInsight[];
}

interface FacilityLevelInsight {
  facilityId: string;
  facilityName: string;
  region: string;
  totalWards: number;
  submissionRate: number;
  avgUtiRate: number;
  avgCabsiRate: number;
  avgVapRate: number;
  highRiskWards: number;
}

interface DistrictLevelInsight {
  district: string;
  hospitals: number;
  cases: number;
  predominantInfection?: string;
  riskSignals: InfectionBreakdownItem[];
}

interface ProvinceLevelInsight {
  region: string;
  facilities: number;
  wards: number;
  submissionRate: number;
  avgUtiRate: number;
  avgCabsiRate: number;
  avgVapRate: number;
}

interface CareLevelInsights {
  patientLevel: PatientLevelInsights;
  wardLevel: WardLevelInsights;
  facilityLevel: FacilityLevelInsight[];
  districtLevel: DistrictLevelInsight[];
  provinceLevel: {
    summaries: ProvinceLevelInsight[];
    nationalRates: {
      uti: number;
      cabsi: number;
      vap: number;
      ssi: number;
    };
  };
}

const defaultInsights: CareLevelInsights = {
  patientLevel: {
    totalPatients: 0,
    patientsWithDevices: 0,
    infectionBreakdown: [],
    topRiskFactors: [],
    antibioticUsage: [],
    microbiologyFindings: []
  },
  wardLevel: {
    complianceRate: 0,
    wards: []
  },
  facilityLevel: [],
  districtLevel: [],
  provinceLevel: {
    summaries: [],
    nationalRates: {
      uti: 0,
      cabsi: 0,
      vap: 0,
      ssi: 0
    }
  }
};

interface UseCareLevelInsightsOptions {
  days?: number;
}

export function useCareLevelInsights(options: UseCareLevelInsightsOptions = {}) {
  const { days = 30 } = options;
  const [insights, setInsights] = useState<CareLevelInsights>(defaultInsights);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadInsights();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const loadInsights = async () => {
    setLoading(true);
    setError(null);

    try {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(endDate.getDate() - days);

      const startStr = startDate.toISOString().split('T')[0];
      const endStr = endDate.toISOString().split('T')[0];

      const [
        patientDocs,
        deviceDocs,
        haiDocs,
        ssiDocs,
        wardDocs,
        facilityDocs,
        hcaiCaseDocs
      ] = await Promise.all([
        getDocs(
          query(
            collection(db, 'patientRecords'),
            where('recordDate', '>=', startStr),
            where('recordDate', '<=', endStr)
          )
        ),
        getDocs(
          query(
            collection(db, 'deviceSnapshots'),
            where('date', '>=', startStr),
            where('date', '<=', endStr)
          )
        ),
        getDocs(
          query(
            collection(db, 'haiRecords'),
            where('periodStart', '<=', endStr),
            where('periodEnd', '>=', startStr)
          )
        ),
        getDocs(
          query(
            collection(db, 'ssiSurveillance'),
            where('periodStart', '<=', endStr),
            where('periodEnd', '>=', startStr)
          )
        ),
        getDocs(collection(db, 'wards')),
        getDocs(collection(db, 'facilities')),
        getDocs(collection(db, 'hcaiCases'))
      ]);

      const patientRecords = patientDocs.docs.map(doc => ({
        id: doc.id,
        ...(doc.data() as any)
      })) as PatientRecord[];

      const deviceSnapshots = deviceDocs.docs.map(doc => ({
        id: doc.id,
        ...(doc.data() as any)
      })) as DeviceSnapshot[];

      const haiRecords = haiDocs.docs.map(doc => ({
        id: doc.id,
        ...(doc.data() as any)
      })) as HCAIRecord[];

      const ssiRecords = ssiDocs.docs.map(doc => ({
        id: doc.id,
        ...(doc.data() as any)
      })) as SSISurveillance[];

      const wards = wardDocs.docs.map(doc => ({
        id: doc.id,
        ...(doc.data() as any)
      })) as Ward[];

      const facilities = facilityDocs.docs.map(doc => ({
        id: doc.id,
        ...(doc.data() as any)
      })) as Facility[];

      const hcaiCases = hcaiCaseDocs.docs.map(doc => ({
        id: doc.id,
        ...(doc.data() as any),
        createdAt: doc.data().createdAt?.toDate?.() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate?.() || new Date()
      })) as HCAISurveillanceCase[];

      const patientLevel = buildPatientLevelInsights(patientRecords, hcaiCases);
      const wardLevel = buildWardLevelInsights(
        wards,
        deviceSnapshots,
        haiRecords,
        ssiRecords,
        hcaiCases,
        days
      );
      const facilityLevel = buildFacilityLevelInsights(facilities, wardLevel.wards);
      const districtLevel = buildDistrictInsights(hcaiCases);
      const provinceLevel = buildProvinceInsights(facilities, wardLevel);

      setInsights({
        patientLevel,
        wardLevel,
        facilityLevel,
        districtLevel,
        provinceLevel
      });
    } catch (err) {
      console.error('Error building care level insights:', err);
      setError(err instanceof Error ? err.message : 'Failed to load insights');
    } finally {
      setLoading(false);
    }
  };

  return useMemo(() => ({
    insights,
    loading,
    error,
    refetch: loadInsights
  }), [insights, loading, error]);
}

function buildPatientLevelInsights(
  patients: PatientRecord[],
  surveillanceCases: HCAISurveillanceCase[]
): PatientLevelInsights {
  const infectionCounts: Record<string, number> = {};
  const riskFactorCounts: Record<string, number> = {};
  const antibioticUsage: Record<string, number> = {};
  const organisms: Record<string, number> = {};

  let patientsWithDevices = 0;

  patients.forEach(patient => {
    if (patient.devices && patient.devices.length > 0) {
      patientsWithDevices += 1;
    }

    (patient.hcaiCases || []).forEach(caseItem => {
      infectionCounts[caseItem.hcaiType] = (infectionCounts[caseItem.hcaiType] || 0) + 1;
    });
  });

  surveillanceCases.forEach(caseItem => {
    const risks = caseItem.riskFactors || {};
    Object.entries(risks).forEach(([key, value]) => {
      if (typeof value === 'boolean' && value) {
        riskFactorCounts[key] = (riskFactorCounts[key] || 0) + 1;
      }
    });

    (caseItem.antibiotics || []).forEach(abx => {
      const indication = abx.indication || 'unspecified';
      antibioticUsage[indication] = (antibioticUsage[indication] || 0) + 1;
    });

    (caseItem.microbiological || []).forEach(micro => {
      const organism = micro.organismIsolated || 'Pending identification';
      organisms[organism] = (organisms[organism] || 0) + 1;
    });
  });

  const toSortedList = (source: Record<string, number>, limit = 6): InfectionBreakdownItem[] =>
    Object.entries(source)
      .sort((a, b) => b[1] - a[1])
      .slice(0, limit)
      .map(([label, count]) => ({ label, count }));

  return {
    totalPatients: patients.length,
    patientsWithDevices,
    infectionBreakdown: toSortedList(infectionCounts),
    topRiskFactors: toSortedList(riskFactorCounts),
    antibioticUsage: toSortedList(antibioticUsage),
    microbiologyFindings: toSortedList(organisms)
  };
}

function buildWardLevelInsights(
  wards: Ward[],
  deviceSnapshots: DeviceSnapshot[],
  haiRecords: HCAIRecord[],
  ssiRecords: SSISurveillance[],
  surveillanceCases: HCAISurveillanceCase[],
  windowDays: number
): WardLevelInsights {
  const activeWards = wards.filter(ward => ward.isActive !== false);

  const snapshotsByWard = groupBy(deviceSnapshots, 'wardId');
  const haiByWard = groupBy(haiRecords, 'wardId');
  const ssiByWard = groupBy(ssiRecords, 'wardId');
  const surveillanceByWard = groupHcaiCasesByWard(surveillanceCases);

  const wardInsights: WardLevelInsight[] = activeWards.map(ward => {
    const snapshots = snapshotsByWard.get(ward.id) || [];
    const wardHAIRecords = haiByWard.get(ward.id) || [];
    const wardSSIRecords = ssiByWard.get(ward.id) || [];
    const wardSurveillanceCases = surveillanceByWard.get(normalizeWardName(ward.name)) || [];

    const deviceDays = calculateDeviceDays(snapshots);
    const aggregatedHAICases = aggregateHCAICases(wardHAIRecords);
    const surveillanceAggregates = aggregateHCAISurveillanceCases(wardSurveillanceCases);
    const hcaiCases = mergeCaseCounts(aggregatedHAICases, surveillanceAggregates);
    const rates = calculateHCAIRates(hcaiCases, deviceDays);

    const totalSsiProcedures = wardSSIRecords.reduce((sum, record) => sum + (record.procedures || 0), 0);
    const totalSsiInfections = wardSSIRecords.reduce((sum, record) => sum + (record.infections || 0), 0);
    const ssiRate = totalSsiProcedures > 0 ? (totalSsiInfections / totalSsiProcedures) * 100 : undefined;

    const compliance = windowDays > 0
      ? Math.min(100, Math.round((snapshots.length / windowDays) * 100))
      : 0;

    return {
      wardId: ward.id,
      wardName: ward.name,
      facilityId: ward.facilityId,
      facilityName: ward.facilityId,
      compliance,
      deviceSnapshots: snapshots.length,
      haiRecords: wardHAIRecords.length,
      ssiRecords: wardSSIRecords.length,
      utiRate: Number(rates.uti.toFixed(2)),
      cabsiRate: Number(rates.cabsi.toFixed(2)),
      vapRate: Number(rates.vap.toFixed(2)),
      ssiRate: ssiRate ? Number(ssiRate.toFixed(2)) : undefined,
      deviceDays,
      hcaiCases,
      ssiProcedures: totalSsiProcedures,
      ssiInfections: totalSsiInfections
    };
  });

  // Compliance rate across wards (mean)
  const complianceRate = wardInsights.length > 0
    ? Math.round(
      wardInsights.reduce((sum, ward) => sum + ward.compliance, 0) / wardInsights.length
    )
    : 0;

  return {
    complianceRate,
    wards: wardInsights
  };
}

const normalizeWardName = (name?: string | null) => name?.trim().toLowerCase() || '';

function groupHcaiCasesByWard(
  cases: HCAISurveillanceCase[]
): Map<string, HCAISurveillanceCase[]> {
  const map = new Map<string, HCAISurveillanceCase[]>();

  cases.forEach(caseItem => {
    const key = normalizeWardName(caseItem.ward);
    if (!key) return;

    if (!map.has(key)) {
      map.set(key, []);
    }
    map.get(key)!.push(caseItem);
  });

  return map;
}

function mergeCaseCounts(
  base: HCAICases,
  additional: HCAICases
): HCAICases {
  const merged: HCAICases = { ...base };

  (Object.keys(additional) as Array<keyof HCAICases>).forEach(key => {
    merged[key] = (merged[key] || 0) + (additional[key] || 0);
  });

  return merged;
}

function buildFacilityLevelInsights(
  facilities: Facility[],
  wardInsights: WardLevelInsight[]
): FacilityLevelInsight[] {
  const facilityMap = new Map<string, Facility>();
  facilities.forEach(facility => facilityMap.set(facility.id, facility));

  const wardsByFacility = groupBy(wardInsights, 'facilityId');
  const insights: FacilityLevelInsight[] = [];

  wardsByFacility.forEach((wardsForFacility, facilityId) => {
    const facility = facilityMap.get(facilityId);
    const facilityName = facility?.name || 'Unassigned';
    const region = facility?.region || 'Unknown';

    const submissionRate = wardsForFacility.length > 0
      ? wardsForFacility.reduce((sum, ward) => sum + ward.compliance, 0) / wardsForFacility.length
      : 0;

    const avg = (prop: keyof WardLevelInsight) => wardsForFacility.length > 0
      ? wardsForFacility.reduce((sum, ward) => sum + ((ward[prop] as number) || 0), 0) / wardsForFacility.length
      : 0;

    const highRiskWards = wardsForFacility.filter(
      ward => ward.utiRate >= 3 || ward.cabsiRate >= 2 || ward.vapRate >= 3
    ).length;

    insights.push({
      facilityId,
      facilityName,
      region,
      totalWards: wardsForFacility.length,
      submissionRate: Number(submissionRate.toFixed(1)),
      avgUtiRate: Number(avg('utiRate').toFixed(2)),
      avgCabsiRate: Number(avg('cabsiRate').toFixed(2)),
      avgVapRate: Number(avg('vapRate').toFixed(2)),
      highRiskWards
    });
  });

  // Update ward facility names for downstream consumers
  wardInsights.forEach(ward => {
    const facility = facilityMap.get(ward.facilityId);
    ward.facilityName = facility?.name || ward.facilityId;
  });

  return insights.sort((a, b) => b.avgUtiRate - a.avgUtiRate);
}

function buildDistrictInsights(hcaiCases: HCAISurveillanceCase[]): DistrictLevelInsight[] {
  const districtMap = new Map<string, {
    hospitals: Set<string>;
    cases: number;
    infections: Record<string, number>;
    riskSignals: Record<string, number>;
  }>();

  hcaiCases.forEach(caseItem => {
    const district = caseItem.district || 'Unknown district';
    if (!districtMap.has(district)) {
      districtMap.set(district, {
        hospitals: new Set<string>(),
        cases: 0,
        infections: {},
        riskSignals: {}
      });
    }

    const entry = districtMap.get(district)!;
    if (caseItem.hospital) {
      entry.hospitals.add(caseItem.hospital);
    }

    const infectionList = caseItem.infections || [];
    entry.cases += infectionList.length;

    infectionList.forEach(infection => {
      if (!infection.infectionType) return;
      entry.infections[infection.infectionType] = (entry.infections[infection.infectionType] || 0) + 1;
    });

    const risks = caseItem.riskFactors || {};
    Object.entries(risks).forEach(([key, value]) => {
      if (typeof value === 'boolean' && value) {
        entry.riskSignals[key] = (entry.riskSignals[key] || 0) + 1;
      }
    });
  });

  const insights: DistrictLevelInsight[] = [];
  districtMap.forEach((value, district) => {
    const predominant = Object.entries(value.infections)
      .sort((a, b) => b[1] - a[1])[0]?.[0];

    insights.push({
      district,
      hospitals: value.hospitals.size,
      cases: value.cases,
      predominantInfection: predominant,
      riskSignals: Object.entries(value.riskSignals)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 4)
        .map(([label, count]) => ({ label, count }))
    });
  });

  return insights.sort((a, b) => b.cases - a.cases);
}

function buildProvinceInsights(
  facilities: Facility[],
  wardLevel: WardLevelInsights
) {
  const provinceMap = new Map<string, {
    facilities: Set<string>;
    wards: number;
    submissionRateSum: number;
    uti: number;
    cabsi: number;
    vap: number;
  }>();

  const facilityById = new Map<string, Facility>();
  facilities.forEach(facility => facilityById.set(facility.id, facility));

  wardLevel.wards.forEach(ward => {
    const facility = facilityById.get(ward.facilityId);
    const region = facility?.region || 'Unknown region';

    if (!provinceMap.has(region)) {
      provinceMap.set(region, {
        facilities: new Set<string>(),
        wards: 0,
        submissionRateSum: 0,
        uti: 0,
        cabsi: 0,
        vap: 0
      });
    }

    const province = provinceMap.get(region)!;
    province.facilities.add(ward.facilityId);
    province.wards += 1;
    province.submissionRateSum += ward.compliance;
    province.uti += ward.utiRate;
    province.cabsi += ward.cabsiRate;
    province.vap += ward.vapRate;
  });

  const summaries: ProvinceLevelInsight[] = [];
  provinceMap.forEach((value, region) => {
    const avgSubmission = value.wards > 0 ? value.submissionRateSum / value.wards : 0;
    summaries.push({
      region,
      facilities: value.facilities.size,
      wards: value.wards,
      submissionRate: Number(avgSubmission.toFixed(1)),
      avgUtiRate: value.wards > 0 ? Number((value.uti / value.wards).toFixed(2)) : 0,
      avgCabsiRate: value.wards > 0 ? Number((value.cabsi / value.wards).toFixed(2)) : 0,
      avgVapRate: value.wards > 0 ? Number((value.vap / value.wards).toFixed(2)) : 0
    });
  });

  const totals = wardLevel.wards.reduce(
    (acc, ward) => {
      acc.deviceDays.cvp += ward.deviceDays.cvp;
      acc.deviceDays.pl += ward.deviceDays.pl;
      acc.deviceDays.urinaryCatheter += ward.deviceDays.urinaryCatheter;
      acc.deviceDays.ventilator += ward.deviceDays.ventilator;
      acc.cases.uti += ward.hcaiCases.uti;
      acc.cases.cabsi += ward.hcaiCases.cabsi;
      acc.cases.vap += ward.hcaiCases.vap;
      acc.ssiProcedures += ward.ssiProcedures || 0;
      acc.ssiInfections += ward.ssiInfections || 0;
      return acc;
    },
    {
      deviceDays: { cvp: 0, pl: 0, urinaryCatheter: 0, ventilator: 0 },
      cases: { uti: 0, cabsi: 0, vap: 0 } as { [key: string]: number },
      ssiProcedures: 0,
      ssiInfections: 0
    }
  );

  const nationalRates = {
    uti: totals.deviceDays.urinaryCatheter > 0
      ? Number(((totals.cases.uti || 0) / totals.deviceDays.urinaryCatheter * 1000).toFixed(2))
      : 0,
    cabsi: totals.deviceDays.cvp > 0
      ? Number(((totals.cases.cabsi || 0) / totals.deviceDays.cvp * 1000).toFixed(2))
      : 0,
    vap: totals.deviceDays.ventilator > 0
      ? Number(((totals.cases.vap || 0) / totals.deviceDays.ventilator * 1000).toFixed(2))
      : 0,
    ssi: totals.ssiProcedures > 0
      ? Number(((totals.ssiInfections / totals.ssiProcedures) * 100).toFixed(2))
      : 0
  };

  return {
    summaries: summaries.sort((a, b) => b.avgUtiRate - a.avgUtiRate),
    nationalRates
  };
}

function groupBy<T extends Record<string, any>>(items: T[], key: keyof T) {
  const map = new Map<string, T[]>();
  items.forEach(item => {
    const value = item[key];
    const mapKey = typeof value === 'string' ? value : '';
    if (!map.has(mapKey)) {
      map.set(mapKey, []);
    }
    map.get(mapKey)!.push(item);
  });
  return map;
}
